import type { TFunction } from "i18next";

/**
 * Human labels for the three identifier vocabularies the product used to print
 * raw (ENG-APP-UX-TUNEUP Track E, floor row 7 — "visible snake_case
 * identifiers"): the element under test (`text_title`), the traffic channel
 * (`organic_search`) and the audit / activity event (`member_invited`). One
 * catalog namespace (`labels.*`, three locales) and one fallback — a
 * title-cased humanization — so an identifier the catalog does not know yet
 * still never reaches the screen as `snake_case`.
 *
 * The raw keys stay the data contract everywhere (filters, joins, API
 * payloads); only the RENDERED text goes through these helpers. The tables map
 * raw keys onto literal catalog suffixes so the strictly key-typed `t()` keeps
 * a literal union (`src/i18n/i18next.d.ts`).
 */

const ELEMENT_KEYS = {
  text_title: "textTitle",
  text_subtitle: "textSubtitle",
  visual_screenshot: "visualScreenshot",
  visual_icon: "visualIcon",
  visual_video: "visualVideo",
  // Console-import bridge vocabulary and the store-listing element ids.
  screenshots: "visualScreenshot",
  screenshot: "visualScreenshot",
  title: "textTitle",
  subtitle: "textSubtitle",
  icon: "visualIcon",
  video: "visualVideo",
  feature_graphic: "featureGraphic",
  long_desc: "longDescription",
  short_desc: "shortDescription",
  // The generatable text placements (`ContentTextElement`).
  text_short_description: "shortDescription",
  text_long_description: "longDescription",
  promo_text: "promoText",
  product_page: "productPage",
  asa_paid_cpp_routing: "asaPaidCppRouting",
  google_ads_paid_cpp_routing: "googleAdsPaidCppRouting",
} as const;

const CHANNEL_KEYS = {
  organic_search: "organicSearch",
  organic_browse: "organicBrowse",
  organic: "organic",
  paid: "paid",
  paid_search: "paidSearch",
  referrer: "referrer",
  all: "all",
  unspecified: "unspecified",
} as const;

const AUDIT_EVENT_KEYS = {
  mode_transitioned: "modeTransitioned",
  project_rule_changed: "projectRuleChanged",
  experiment_finalized: "experimentFinalized",
  source_calibration_updated: "sourceCalibrationUpdated",
  policy_source_drift_detected: "policySourceDriftDetected",
  industry_pattern_refreshed: "industryPatternRefreshed",
  scheduled_iteration_fired: "scheduledIterationFired",
  signal_updated: "signalUpdated",
  signal_matured: "signalMatured",
  app_created: "appCreated",
  app_deleted: "appDeleted",
  signal_suppression_changed: "signalSuppressionChanged",
  autopilot_setting_changed: "autopilotSettingChanged",
  auto_apply_winner_changed: "autoApplyWinnerChanged",
  winner_auto_applied: "winnerAutoApplied",
  appetite_changed: "appetiteChanged",
  member_invited: "memberInvited",
  member_role_changed: "memberRoleChanged",
  member_removed: "memberRemoved",
  api_key_created: "apiKeyCreated",
  api_key_revoked: "apiKeyRevoked",
  integration_connected: "integrationConnected",
  integration_disconnected: "integrationDisconnected",
  store_connection_reauthenticated: "storeConnectionReauthenticated",
  billing_plan_changed: "billingPlanChanged",
  compliance_addon_toggled: "complianceAddonToggled",
  managed_image_addon_toggled: "managedImageAddonToggled",
  region_pin_changed: "regionPinChanged",
  retention_sweep_executed: "retentionSweepExecuted",
  operator_signal_created: "operatorSignalCreated",
  operator_signal_edited: "operatorSignalEdited",
  operator_signal_deleted: "operatorSignalDeleted",
  org_rule_changed: "orgRuleChanged",
  competitor_pattern_added: "competitorPatternAdded",
  competitor_update: "competitorUpdate",
  project_rule: "projectRule",
  api_key: "apiKey",
  workspace_settings: "workspaceSettings",
  ai_behavior: "aiBehavior",
  // ENG-APP-UX-TUNEUP C10 M3-R25-5: every projected audit type has its own
  // words -- the humanizer is never the label of a type the roster lists.
  app_claim_acquired: "appClaimAcquired",
  app_claim_force_released: "appClaimForceReleased",
  app_claim_released: "appClaimReleased",
  app_claim_released_inactivity: "appClaimReleasedInactivity",
  app_ownership_verified: "appOwnershipVerified",
  app_paired: "appPaired",
  app_unpaired: "appUnpaired",
  dashboard_token_issued: "dashboardTokenIssued",
  dashboard_token_revoked: "dashboardTokenRevoked",
  dashboard_token_revoked_system: "dashboardTokenRevokedSystem",
  dashboard_token_rotated: "dashboardTokenRotated",
  experiment_visual_upload_deferred: "experimentVisualUploadDeferred",
  experiment_visual_upload_failed: "experimentVisualUploadFailed",
  experiment_visual_uploaded: "experimentVisualUploaded",
  generation_evidence_appended: "generationEvidenceAppended",
  operator_candidate_override: "operatorCandidateOverride",
  trial_converted: "trialConverted",
  winner_rollback_applied: "winnerRollbackApplied",
  winner_rollback_failed: "winnerRollbackFailed",
  workspace_branding_updated: "workspaceBrandingUpdated",
  workspace_signup_state_changed: "workspaceSignupStateChanged",
  cross_app_aggregation_completed: "crossAppAggregationCompleted",
  cross_app_pattern_contested: "crossAppPatternContested",
  live_connect_session_captured: "liveConnectSessionCaptured",
  audit_log_exported: "auditLogExported",
  winner_auto_apply_skipped: "winnerAutoApplySkipped",
  winner_auto_apply_failed: "winnerAutoApplyFailed",
} as const;

const RESOURCE_KEYS = {
  ui_action: "uiAction",
  api_action: "apiAction",
  system_action: "systemAction",
  webhook: "webhook",
  app: "app",
  experiment: "experiment",
  signal: "signal",
  project_rule: "projectRule",
  member: "member",
  api_key: "apiKey",
  billing: "billing",
  integration: "integration",
  workspace_settings: "workspaceSettings",
  ai_behavior: "aiBehavior",
  csv: "csv",
  ndjson: "ndjson",
} as const;

type ElementCatalogKey = (typeof ELEMENT_KEYS)[keyof typeof ELEMENT_KEYS];
type ResourceCatalogKey = (typeof RESOURCE_KEYS)[keyof typeof RESOURCE_KEYS];
type ChannelCatalogKey = (typeof CHANNEL_KEYS)[keyof typeof CHANNEL_KEYS];
type AuditEventCatalogKey =
  (typeof AUDIT_EVENT_KEYS)[keyof typeof AUDIT_EVENT_KEYS];

/**
 * The icon family across the writers' vocabularies (`visual_icon`, the
 * console-import `icon`): the one question the Apple icon limitation asks
 * (C10 M3-R06-2) -- a single writer's token is not the family.
 */
export function isIconElement(element: string | null | undefined): boolean {
  return (
    element != null &&
    (ELEMENT_KEYS as Record<string, string>)[element] === "visualIcon"
  );
}

/** `some_raw_key` → `Some raw key` — the last resort, never `snake_case`. */
export function humanizeIdentifier(raw: string): string {
  const words = raw
    .trim()
    .split(/[_\s-]+/)
    .filter(Boolean);
  if (words.length === 0) return raw;
  return words
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase(),
    )
    .join(" ");
}

/** The element under test — `text_title` → "Title". */
export function elementLabel(
  t: TFunction,
  raw: string | null | undefined,
): string {
  if (!raw) return "";
  const key = (ELEMENT_KEYS as Record<string, ElementCatalogKey | undefined>)[
    raw
  ];
  return key ? t(`labels.element.${key}`) : humanizeIdentifier(raw);
}

/** The traffic channel — `organic_search` → "Organic search". */
export function channelLabel(
  t: TFunction,
  raw: string | null | undefined,
): string {
  if (!raw) return "";
  const key = (CHANNEL_KEYS as Record<string, ChannelCatalogKey | undefined>)[
    raw
  ];
  return key ? t(`labels.channel.${key}`) : humanizeIdentifier(raw);
}

/** An audit-log / activity event — `member_invited` → "Member invited". */
export function auditEventLabel(
  t: TFunction,
  raw: string | null | undefined,
): string {
  if (!raw) return "";
  const key = (
    AUDIT_EVENT_KEYS as Record<string, AuditEventCatalogKey | undefined>
  )[raw];
  return key ? t(`labels.auditEvent.${key}`) : humanizeIdentifier(raw);
}

/** An audit-log resource (the actor / object class) — `project_rule` → "Project rule". */
export function resourceTypeLabel(
  t: TFunction,
  raw: string | null | undefined,
): string {
  if (!raw) return "";
  const key = (RESOURCE_KEYS as Record<string, ResourceCatalogKey | undefined>)[
    raw
  ];
  return key ? t(`labels.resource.${key}`) : humanizeIdentifier(raw);
}

/** The catalog suffixes every locale MUST carry under `labels.*`. */
export const LABEL_CATALOG_KEYS = {
  element: [...new Set(Object.values(ELEMENT_KEYS))],
  channel: [...new Set(Object.values(CHANNEL_KEYS))],
  auditEvent: [...new Set(Object.values(AUDIT_EVENT_KEYS))],
  resource: [...new Set(Object.values(RESOURCE_KEYS))],
} as const;
